import { Engine, Scene, ArcRotateCamera, Vector3, HemisphericLight, SpotLight, DirectionalLight, PointLight, SceneLoader, CubeTexture, Color4, PBRMaterial, Color3, AbstractMesh, Texture, TransformNode, GlowLayer, NodeMaterial, Material, BaseTexture } from '@babylonjs/core';
import "@babylonjs/loaders/glTF";
import { KNOB_CONFIGURATION, KnobMaterial, SPECIAL_MATERIALS } from '@/config/knobConfig';
import { ENCLOSURE_MATERIALS, EnclosureMaterial, ENCLOSURE_HARDWARE_MATERIALS, ENCLOSURE_CHILD_MESH_MATERIALS } from '@/config/enclosureConfig';
import { LED_MATERIALS, LEDMaterial } from '@/config/ledConfig';

export class BabylonScene {
    private engine: Engine;
    private scene: Scene;
    private canvas: HTMLCanvasElement;
    private camera!: ArcRotateCamera;
    private currentKnobType = 'classic';
    private currentMaterial = 'vintage_offwhite';
    private currentEnclosureMaterial = 'brushed_silver';
    private currentLEDMaterial = 'led_red';
    private knobMeshes: AbstractMesh[] = [];
    private enclosureMesh: AbstractMesh | null = null;
    private ledMesh: AbstractMesh | null = null;
    private knobMaterials: Map<string, Material> = new Map();
    private enclosureMaterials: Map<string, Material> = new Map();
    private hardwareMaterials: Map<string, Material> = new Map();
    private enclosureChildMaterials: Map<string, NodeMaterial> = new Map();
    private specialMaterials: Map<string, PBRMaterial> = new Map();
    private ledMaterials: Map<string, PBRMaterial> = new Map();
    private childMeshNodeMaterials: Map<string, NodeMaterial> = new Map();
    private glowLayer: GlowLayer | null = null;

    // Knob model caching: we build each knob type once, then show/hide instantly.
    private knobTypeNodes: Map<string, { parent: TransformNode; meshes: AbstractMesh[] }> = new Map();
    private currentKnobsParent: TransformNode | null = null;

    // Preload guard
    private hasStartedPreload = false;
    
    // Simple loading state management
    private pendingTextureLoads = 0;
    private currentOperationId = 0;
    private onLoadingChange?: (isLoading: boolean) => void;
    private hardLoading = true; // keep overlay until full preload complete

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        
        // Add device pixel ratio support for high-DPI displays (like iPhone)
        const engineOptions = {
            preserveDrawingBuffer: true,
            stencil: true,
            antialias: true,
            adaptToDeviceRatio: true
        };
        
        this.engine = new Engine(this.canvas, true, engineOptions);
        
        // Detect device type and set appropriate scaling
        const isIPhone = /iPhone/.test(navigator.userAgent);
        const scalingLevel = isIPhone ? 0.325 : .8;
        this.engine.setHardwareScalingLevel(scalingLevel);
        
        this.scene = new Scene(this.engine);
        // Keep scene image processing enabled so the final output is correctly displayed in sRGB/gamma.
        // Disabling it can make textures look "linear" (darker), like toggling "Convert to linear space"
        // in the Node Material Editor.
        
        // Set scene clear color to pure white
        this.scene.clearColor = new Color4(1, 1, 1, 1);
        
        this.createScene().catch(console.error);

        // Handle window resize
        window.addEventListener('resize', () => {
            this.engine.resize();
        });
    }

    async createScene(): Promise<void> {
        console.log('Creating scene...');
        // Hold the loading overlay from the very start of scene creation.
        this.hardLoading = true;
        this.emitLoadingState();
        const isMobile =
            window.matchMedia?.('(max-width: 768px)')?.matches ||
            /iPhone|iPad|iPod/i.test(navigator.userAgent);

        // Dedicated default zoom for iPhone/mobile (slightly more zoomed out)
        const initialRadius = isMobile ? 1.35 : 1.15;

        // Dedicated default starting view for iPhone/mobile
        const initialAlpha = isMobile ? (Math.PI / 2.3) : (Math.PI / 2.1);
        const initialBeta = isMobile ? (Math.PI / 2.02) : (Math.PI / 2.2);
        const initialTarget = Vector3.Zero();

        // Arc Rotate Camera (orbits around a target)
        this.camera = new ArcRotateCamera(
            'camera1', 
            initialAlpha,                  // alpha (rotation around y-axis) - start from front
            initialBeta,       // beta (rotation around x-axis)
            initialRadius,        // radius (distance from target)
            initialTarget,    // target
            this.scene
        );
        
        // Enable camera controls
        this.camera.attachControl(this.canvas, true);

        // Slow rotation (drag) speed by ~40%
        // Higher values = less sensitive / slower rotation
        // Additional ~40% slower total (another +20%)
        // +25% sensibility → ~20% slower spin than previous (5557 * 1.25)
        // Mobile: 30% faster spin than desktop (lower value = faster rotation)
        this.camera.angularSensibilityX = isMobile ? 3890 : 14355;
        this.camera.angularSensibilityY = isMobile ? 3890 : 14355;
        
        // Disable panning
        this.camera.panningSensibility = 0;
        
        // Set zoom sensitivity (wheel sensitivity)
        // Slow zoom speed by ~40%
        this.camera.wheelPrecision = 70; // Higher values = less sensitive zoom
        // Additional ~35% slower
        this.camera.wheelDeltaPercentage = 0.00195; // Percentage of radius change per wheel step

        // Mobile: slow pinch-to-zoom by 50% (pinchPrecision higher = slower, default is 12)
        if (isMobile) {
            this.camera.pinchPrecision = 125;
        }
        
        // Set some camera limits for better user experience
        // Mobile: prevent zooming in too close (keeps framing usable on iPhone)
        this.camera.lowerRadiusLimit = isMobile ? 1.35 : 1.1;
        this.camera.upperRadiusLimit = isMobile ? 1.35 : 1.17;
        
        // Set near plane to allow closer viewing without clipping
        this.camera.minZ = 0.05;
        
        // Auto-rotation disabled
        this.camera.useAutoRotationBehavior = false;

        // Create and setup environment
        const envTexture = CubeTexture.CreateFromPrefilteredData(
            "/environment/HDRI.env",
            this.scene
        );
        
        // Rotate the HDRI to position lights
        envTexture.rotationY = 0;
        
        // Use HDR texture for environment lighting
        this.scene.environmentTexture = envTexture;
        
        // Increase environment intensity significantly for even flake visibility
        this.scene.environmentIntensity = .45;
        

        // Soft ambient light from above
        const light = new HemisphericLight('light1', new Vector3(0, 1, 0), this.scene);
        light.intensity = .15;  // Soft ambient light

        // Soft ambient light from below to illuminate back and bottom
        // const backLight = new HemisphericLight('light2', new Vector3(0, -1, 0), this.scene);
        // backLight.intensity = 1;  // Gentle fill light from below
        
        // Very subtle front light
        // const frontLight = new HemisphericLight('light3', new Vector3(0, 0, 1), this.scene);
        // frontLight.intensity = .1;  // Very soft front fill

        // Spot light pointing at the front of the pedal
        const spotLight = new SpotLight(
            'spotLight',
            new Vector3(0, 0, .5),       // position: WHERE the light source is located (x, y, z)
            new Vector3(0, 0, -1),     // direction: WHICH WAY the light points (normalized vector)
            Math.PI*11,                // angle: HOW WIDE the cone is (in radians) - larger = wider cone
            .1,                          // exponent: HOW SOFT the edges are - lower = softer falloff, higher = harder edges
            this.scene
        );
        spotLight.intensity = 0;          // intensity: HOW BRIGHT the light is (multiplier)
        spotLight.range = 3;             // range: HOW FAR the light reaches before fading to zero

        // Directional light (simulates sunlight) pointing at the front
        // const directionalLight = new DirectionalLight(
        //     'directionalLight',
        //     new Vector3(-0.3, -0.5, -1),  // Direction: angled from upper front
        // //     this.scene
        // );
        // directionalLight.intensity = 0.5;

        // Point light positioned in front of the pedal
        // const pointLight = new PointLight(
        //     'pointLight',
        //     new Vector3(0, 0.3, 1),       // Position: slightly above center front
        //     this.scene
        // );
        // pointLight.intensity = 0.6;
        // pointLight.range = 5;

        // Create glow layer for LED effects
        this.glowLayer = new GlowLayer("ledGlow", this.scene);
        this.glowLayer.intensity = 1.2;  // Moderate intensity for LED glow
        this.glowLayer.blurKernelSize = 9;  // Low kernel size for performance
        console.log('Glow layer created with kernel size:', this.glowLayer.blurKernelSize);

        console.log('Scene created, pre-loading hardware materials...');
        // Pre-load hardware materials (especially node materials) and WAIT for them to load
        await this.preloadHardwareMaterials();
        console.log('Hardware materials loaded, now loading models...');
        
        // Build/enable the initial knob type synchronously so the scene is usable immediately.
        await this.ensureKnobTypeBuilt(this.currentKnobType, true);
        await this.loadEnclosureModel();
        // LED is now a child mesh of the enclosure model (no separate load needed)
        // await this.loadLEDModel();

        // Preload all selectable variants BEFORE we dismiss the loading overlay.
        // This avoids the "default loads, then other options pop in" effect.
        await this.preloadAllSelectableAssets();

        // Release the hard loading lock. If there are still transient texture loads,
        // the overlay will remain until those finish.
        this.hardLoading = false;
        this.emitLoadingState();
    }

    async switchKnobType(knobTypeId: string): Promise<void> {
        console.log(`Switching to knob type: ${knobTypeId}`);
        const knobType = KNOB_CONFIGURATION.find(k => k.id === knobTypeId);
        if (!knobType) {
            console.error(`Knob type ${knobTypeId} not found`);
            return;
        }

        // IMPORTANT: update current knob type FIRST so any material application
        // during this transition resolves against the correct knob type.
        this.currentKnobType = knobTypeId;

        // Disable current knob group (keep it cached for instant switching later)
        if (this.currentKnobsParent) {
            this.currentKnobsParent.setEnabled(false);
        }

        // Build (if needed) + enable next knob group
        await this.ensureKnobTypeBuilt(knobTypeId, true);

        // Reset knob material to the first option for this knob type (matches previous behavior)
        if (knobType.materials.length > 0) {
            await this.switchMaterial(knobType.materials[0].id);
        }
    }

    async switchMaterial(materialId: string): Promise<void> {
        console.log(`Switching to material: ${materialId}`);
        const knobType = KNOB_CONFIGURATION.find(k => k.id === this.currentKnobType);
        const material = knobType?.materials.find(m => m.id === materialId);
        
        if (!material) {
            console.error(`Material ${materialId} not found for knob type ${this.currentKnobType}`);
            return;
        }

        // Get or create main knob material
        let knobMaterial = this.knobMaterials.get(materialId);
        if (!knobMaterial) {
            knobMaterial = await this.createMaterialFromConfig(material);
            this.knobMaterials.set(materialId, knobMaterial);
        }

        // Prepare special materials map
        const specialMaterialsMap = new Map<string, PBRMaterial>();

        // Add indicator material if specified
        if (material.indicatorMaterial) {
            const indicatorKey = `special_${material.indicatorMaterial}`;
            let indicatorMaterial = this.specialMaterials.get(indicatorKey);
            
            if (!indicatorMaterial) {
                indicatorMaterial = this.createSpecialMaterial(material.indicatorMaterial);
                this.specialMaterials.set(indicatorKey, indicatorMaterial);
            }
            
            specialMaterialsMap.set('indicator', indicatorMaterial);
        }

        // Add child mesh materials if specified
        if (material.childMeshMaterials) {
            for (const [meshName, materialType] of Object.entries(material.childMeshMaterials)) {
                const specialKey = `special_${materialType}`;
                let specialMaterial = this.specialMaterials.get(specialKey);
                
                if (!specialMaterial) {
                    specialMaterial = this.createSpecialMaterial(materialType);
                    this.specialMaterials.set(specialKey, specialMaterial);
                }
                
                specialMaterialsMap.set(meshName, specialMaterial);
            }
        }

        // Prepare child mesh node materials map
        const childMeshNodeMaterialsMap = new Map<string, NodeMaterial>();

        // Load child mesh node materials if specified
        if (material.childMeshNodeMaterials) {
            for (const [meshName, nodeMaterialConfig] of Object.entries(material.childMeshNodeMaterials)) {
                const materialKey = `${materialId}_${meshName}`;
                let childNodeMaterial = this.childMeshNodeMaterials.get(materialKey);
                
                if (!childNodeMaterial) {
                    try {
                        console.log(`Loading child mesh node material for ${meshName} from ${nodeMaterialConfig.nodeMaterialPath}`);
                        childNodeMaterial = await this.loadNodeMaterial(materialKey, nodeMaterialConfig.nodeMaterialPath);
                        this.childMeshNodeMaterials.set(materialKey, childNodeMaterial);
                    } catch (error) {
                        console.error(`Failed to load child mesh node material for ${meshName}, skipping:`, error);
                        continue;
                    }
                }
                
                childMeshNodeMaterialsMap.set(meshName, childNodeMaterial);
            }
        }

        // Apply materials to all knob meshes
        console.log(`Applying material ${materialId} to ${this.knobMeshes.length} knob meshes`);
        this.knobMeshes.forEach((mesh, index) => {
            console.log(`Applying material to knob ${index}: ${mesh.name}`);
            this.applyMaterialsToMesh(mesh, knobMaterial!, specialMaterialsMap, childMeshNodeMaterialsMap);
            // Ensure mesh remains visible
            mesh.isVisible = true;
            mesh.setEnabled(true);
        });

        this.currentMaterial = materialId;
    }

    async switchEnclosureMaterial(materialId: string): Promise<void> {
        console.log(`Switching enclosure to material: ${materialId}`);
        const material = ENCLOSURE_MATERIALS.find(m => m.id === materialId);
        
        if (!material) {
            console.error(`Enclosure material ${materialId} not found`);
            return;
        }

        // Check if this is a Node Material
        if (material.useNodeMaterial && material.nodeMaterialPath) {
            // Check if already loaded
            let nodeMaterial = this.enclosureMaterials.get(materialId) as NodeMaterial | undefined;
            
            if (!nodeMaterial) {
                // Start loading operation
                const operationId = this.startLoadingOperation();
                
                try {
                    nodeMaterial = await this.loadNodeMaterial(materialId, material.nodeMaterialPath);
                    this.enclosureMaterials.set(materialId, nodeMaterial);
                    
                    // End loading operation
                    if (this.onLoadingChange) {
                        this.onLoadingChange(false);
                    }
                } catch (error) {
                    console.error('Failed to load Node Material, using fallback PBR material');
                    // Fallback to PBR material
                    const pbrMaterial = this.createEnclosureMaterialFromConfig(material, operationId);
                    this.enclosureMaterials.set(materialId, pbrMaterial);
                    nodeMaterial = pbrMaterial as any;
                    
                    if (this.onLoadingChange) {
                        this.onLoadingChange(false);
                    }
                }
            }
            
             // Set environment intensity on PBR block if it exists
            if (nodeMaterial && material.properties.environmentIntensity !== undefined) {
                const pbrBlock = nodeMaterial.getBlockByName("PBRMetallicRoughness");
                if (pbrBlock) {
                    (pbrBlock as any).environmentIntensity = material.properties.environmentIntensity;
                    console.log(`Set PBR block environment intensity to: ${material.properties.environmentIntensity}`);
                }
            }
            
            // Apply Node Material
            if (this.enclosureMesh && nodeMaterial) {
                console.log('Applying Node Material to enclosure:', materialId);
                this.enclosureMesh.material = nodeMaterial;

                // Apply per-child-mesh node materials from shared config
                this.enclosureMesh.getChildMeshes().forEach(childMesh => {
                    const meshNameLower = childMesh.name.toLowerCase();
                    // LED is controlled independently by switchLEDMaterial
                    if (meshNameLower.includes('led')) return;
                    const matchedKey = Object.keys(ENCLOSURE_CHILD_MESH_MATERIALS).find(key =>
                        meshNameLower.includes(key.toLowerCase())
                    );
                    if (matchedKey) {
                        const cfg = ENCLOSURE_CHILD_MESH_MATERIALS[matchedKey];
                        const childMat = this.enclosureChildMaterials.get(cfg.nodeMaterialPath);
                        if (childMat) {
                            if (cfg.environmentIntensity !== undefined) {
                                const pbrBlock = childMat.getBlockByName("PBRMetallicRoughness");
                                if (pbrBlock) (pbrBlock as any).environmentIntensity = cfg.environmentIntensity;
                            }
                            childMesh.material = childMat;
                            return;
                        }
                    }
                    // Fallback: apply main enclosure material
                    childMesh.material = nodeMaterial!;
                });
            }
            
            this.currentEnclosureMaterial = materialId;
            return;
        }

        // Standard PBR Material handling (existing code)
        // Check if this material has textures that need loading
        const hasTextures = material.properties.albedoTexture || 
                           material.properties.normalTexture || 
                           material.properties.metallicRoughnessTexture || 
                           material.properties.aoTexture ||
                           material.properties.bumpTexture;

        let operationId: number | null = null;
        
        // Start loading operation if material has textures and isn't cached
        if (hasTextures && !this.enclosureMaterials.has(materialId)) {
            operationId = this.startLoadingOperation();
            
            // Safety timeout to prevent getting stuck
            setTimeout(() => {
                if (operationId === this.currentOperationId && this.pendingTextureLoads > 0) {
                    console.warn('Loading timeout reached, forcing completion');
                    this.pendingTextureLoads = 0;
                    if (this.onLoadingChange) {
                        this.onLoadingChange(false);
                    }
                }
            }, 8000); // 8 second safety timeout
        }

        // Get or create enclosure material
        let pbrMaterial = this.enclosureMaterials.get(materialId) as PBRMaterial | undefined;
        if (!pbrMaterial) {
            pbrMaterial = this.createEnclosureMaterialFromConfig(material, operationId);
            this.enclosureMaterials.set(materialId, pbrMaterial);
        }

        // Apply material to enclosure mesh if it exists
        if (this.enclosureMesh && pbrMaterial) {
            console.log('Applying material to enclosure:', materialId);
            this.enclosureMesh.material = pbrMaterial;

            // Apply per-child-mesh node materials from shared config
            this.enclosureMesh.getChildMeshes().forEach(childMesh => {
                const meshNameLower = childMesh.name.toLowerCase();
                // LED is controlled independently by switchLEDMaterial
                if (meshNameLower.includes('led')) return;
                const matchedKey = Object.keys(ENCLOSURE_CHILD_MESH_MATERIALS).find(key =>
                    meshNameLower.includes(key.toLowerCase())
                );
                if (matchedKey) {
                    const cfg = ENCLOSURE_CHILD_MESH_MATERIALS[matchedKey];
                    const childMat = this.enclosureChildMaterials.get(cfg.nodeMaterialPath);
                    if (childMat) {
                        if (cfg.environmentIntensity !== undefined) {
                            const pbrBlock = childMat.getBlockByName("PBRMetallicRoughness");
                            if (pbrBlock) (pbrBlock as any).environmentIntensity = cfg.environmentIntensity;
                        }
                        childMesh.material = childMat;
                        return;
                    }
                }
                // Fallback: apply main enclosure material
                childMesh.material = pbrMaterial!;
            });
        } else {
            console.error('No enclosure mesh found to apply material to');
        }

        this.currentEnclosureMaterial = materialId;
    }

    switchLEDMaterial(materialId: string): void {
        console.log(`Switching LED to material: ${materialId}`);
        const material = LED_MATERIALS.find(m => m.id === materialId);
        
        if (!material) {
            console.error(`LED material ${materialId} not found`);
            return;
        }

        // Get or create LED material
        let pbrMaterial = this.ledMaterials.get(materialId);
        if (!pbrMaterial) {
            pbrMaterial = this.createLEDMaterialFromConfig(material);
            this.ledMaterials.set(materialId, pbrMaterial);
        }

        // Find the LED child mesh on the enclosure model
        const ledChildMesh = this.enclosureMesh
            ? this.enclosureMesh.getChildMeshes().find(m => m.name.toLowerCase().includes('led'))
            : null;

        if (ledChildMesh && pbrMaterial) {
            console.log('Applying material to LED child mesh:', materialId);
            ledChildMesh.material = pbrMaterial;
        } else {
            console.error('No LED child mesh found on enclosure to apply material to');
        }

        this.currentLEDMaterial = materialId;
    }

    private async createMaterialFromConfig(materialConfig: KnobMaterial): Promise<Material> {
        console.log(`Creating material: ${materialConfig.displayName}`);
        
        // Check if this is a Node Material
        if (materialConfig.useNodeMaterial && materialConfig.nodeMaterialPath) {
            console.log(`Loading Node Material for knob: ${materialConfig.id}`);
            try {
                const nodeMaterial = await this.loadNodeMaterial(materialConfig.id, materialConfig.nodeMaterialPath);
                console.log(`Node Material loaded successfully for knob: ${materialConfig.id}`);
                
                // Set environment intensity on PBR block if it exists
                if (nodeMaterial && materialConfig.properties.environmentIntensity !== undefined) {
                    const pbrBlock = nodeMaterial.getBlockByName("PBRMetallicRoughness");
                    if (pbrBlock) {
                        (pbrBlock as any).environmentIntensity = materialConfig.properties.environmentIntensity;
                        console.log(`Set PBR block environment intensity to: ${materialConfig.properties.environmentIntensity}`);
                    }
                }
                
                return nodeMaterial;
            } catch (error) {
                console.error(`Failed to load Node Material for knob ${materialConfig.id}, using PBR fallback:`, error);
                // Fall back to PBR material
            }
        }
        
        // Create PBR material
        const material = new PBRMaterial(materialConfig.id, this.scene);
        const props = materialConfig.properties;
        
        material.albedoColor = new Color3(props.albedoColor[0], props.albedoColor[1], props.albedoColor[2]);
        material.metallic = props.metallic;
        material.roughness = props.roughness;
        material.environmentIntensity = props.environmentIntensity || 0.7;
        
        // Load texture if specified
        if (props.textureUrl) {
            material.albedoTexture = new Texture(props.textureUrl, this.scene);
        }
        
        return material;
    }

    private async preloadAllSelectableAssets(): Promise<void> {
        if (this.hasStartedPreload) return;
        this.hasStartedPreload = true;

        console.log('Preloading selectable knob/enclosure/LED assets...');

        // 1) Preload all knob materials (including child mesh node materials)
        const allKnobMaterials: KnobMaterial[] = KNOB_CONFIGURATION.flatMap(k => k.materials);
        await Promise.all(allKnobMaterials.map(async (mat) => {
            // Main knob material
            if (!this.knobMaterials.has(mat.id)) {
                const built = await this.createMaterialFromConfig(mat);
                this.knobMaterials.set(mat.id, built);
                await this.waitForMaterialTextures(built);
            }

            // Child mesh node materials
            if (mat.childMeshNodeMaterials) {
                await Promise.all(Object.entries(mat.childMeshNodeMaterials).map(async ([meshName, cfg]) => {
                    const key = `${mat.id}_${meshName}`;
                    if (!this.childMeshNodeMaterials.has(key)) {
                        try {
                            const nm = await this.loadNodeMaterial(key, cfg.nodeMaterialPath);
                            this.childMeshNodeMaterials.set(key, nm);
                            await this.waitForMaterialTextures(nm);
                        } catch (e) {
                            console.warn(`Preload: failed child node material ${key}`, e);
                        }
                    }
                }));
            }
        }));

        // 2) Preload all enclosure materials (node materials + PBR textures)
        await Promise.all(ENCLOSURE_MATERIALS.map(async (mat) => {
            if (this.enclosureMaterials.has(mat.id)) return;

            if (mat.useNodeMaterial && mat.nodeMaterialPath) {
                try {
                    const nm = await this.loadNodeMaterial(mat.id, mat.nodeMaterialPath);

                    // Apply env intensity to node material if present
                    if (mat.properties.environmentIntensity !== undefined) {
                        const pbrBlock = nm.getBlockByName("PBRMetallicRoughness");
                        if (pbrBlock) {
                            (pbrBlock as any).environmentIntensity = mat.properties.environmentIntensity;
                        }
                    }

                    this.enclosureMaterials.set(mat.id, nm);
                    await this.waitForMaterialTextures(nm);
                    return;
                } catch (e) {
                    console.warn(`Preload: failed enclosure node material ${mat.id}, falling back to PBR`, e);
                }
            }

            // PBR fallback (or non-node materials)
            const pbr = this.createEnclosureMaterialFromConfig(mat, null);
            this.enclosureMaterials.set(mat.id, pbr);
            await this.waitForMaterialTextures(pbr);
        }));

        // 3) Preload all LED materials (cheap, but ensures no hiccups)
        for (const mat of LED_MATERIALS) {
            if (!this.ledMaterials.has(mat.id)) {
                const pbr = this.createLEDMaterialFromConfig(mat);
                this.ledMaterials.set(mat.id, pbr);
            }
        }

        // 4) Preload all knob type models (build them disabled so we can switch instantly)
        const otherKnobTypeIds = KNOB_CONFIGURATION.map(k => k.id).filter(id => id !== this.currentKnobType);
        await Promise.all(otherKnobTypeIds.map(async (id) => {
            await this.ensureKnobTypeBuilt(id, false);
        }));

        console.log('Selectable asset preload complete.');
    }

    private async waitForMaterialTextures(material: Material): Promise<void> {
        // Best-effort: wait for any active textures to finish loading.
        // This reduces the "first click" delay when a material is applied.
        if (!material.getActiveTextures) return;
        const textures = material.getActiveTextures() || [];
        await Promise.all(textures.map((t) => this.waitForTextureReady(t)));
    }

    private async waitForTextureReady(tex: BaseTexture): Promise<void> {
        // Some textures (CubeTexture, etc.) may not expose onLoadObservable.
        try {
            if ((tex as any).isReady && typeof (tex as any).isReady === 'function' && (tex as any).isReady()) {
                return;
            }
        } catch {
            // ignore
        }

        const anyTex = tex as any;
        await new Promise<void>((resolve) => {
            let resolved = false;
            const done = () => {
                if (resolved) return;
                resolved = true;
                resolve();
            };

            // Fallback timeout so preload never blocks forever
            setTimeout(done, 8000);

            if (anyTex?.onLoadObservable?.addOnce) {
                anyTex.onLoadObservable.addOnce(done);
            }
            if (anyTex?.onErrorObservable?.addOnce) {
                anyTex.onErrorObservable.addOnce(done);
            }
        });
    }

    private createSpecialMaterial(type: 'black' | 'white' | 'metallic'): PBRMaterial {
        const config = SPECIAL_MATERIALS[type];
        const material = new PBRMaterial(config.id, this.scene);
        const props = config.properties;
        
        material.albedoColor = new Color3(props.albedoColor[0], props.albedoColor[1], props.albedoColor[2]);
        material.metallic = props.metallic;
        material.roughness = props.roughness;
        material.environmentIntensity = props.environmentIntensity;
        
        return material;
    }

    private async loadNodeMaterial(materialId: string, jsonPath: string): Promise<NodeMaterial> {
        console.log(`Loading Node Material: ${materialId} from ${jsonPath}`);
        
        try {
            const nodeMaterial = await NodeMaterial.ParseFromFileAsync(
                materialId,
                jsonPath,
                this.scene
            );
            
            // Build the node material - this will compile the shader
            nodeMaterial.build(false);
            
            console.log(`Node Material ${materialId} loaded and built successfully`);
            
            return nodeMaterial;
        } catch (error) {
            console.error(`Error loading Node Material ${materialId}:`, error);
            throw error;
        }
    }

    private createEnclosureMaterialFromConfig(materialConfig: EnclosureMaterial, operationId: number | null): PBRMaterial {
        console.log(`Creating enclosure material: ${materialConfig.displayName}`);
        const material = new PBRMaterial(materialConfig.id, this.scene);
        const props = materialConfig.properties;
        
        material.albedoColor = new Color3(props.albedoColor[0], props.albedoColor[1], props.albedoColor[2]);
        material.metallic = props.metallic;
        material.roughness = props.roughness;
        material.environmentIntensity = props.environmentIntensity || 0.7;
        
        // Apply clear coat if specified
        if (props.clearCoatIntensity !== undefined) {
            material.clearCoat.isEnabled = true;
            material.clearCoat.intensity = props.clearCoatIntensity;
            material.clearCoat.roughness = props.clearCoatRoughness || 0.0;
            console.log(`Clear coat enabled: intensity=${props.clearCoatIntensity}, roughness=${props.clearCoatRoughness}`);
        }
        
        // Load texture maps if specified
        if (props.albedoTexture) {
            const albedoTexture = new Texture(props.albedoTexture, this.scene);
            albedoTexture.uScale = props.albedoTextureUV?.u ?? 8.0;
            albedoTexture.vScale = props.albedoTextureUV?.v ?? 8.5;
            material.albedoTexture = albedoTexture;
            
            if (operationId !== null) {
                this.trackTextureLoad(operationId);
                albedoTexture.onLoadObservable.add(() => {
                    this.onTextureLoaded(operationId);
                });
            }
        }
        
        if (props.normalTexture) {
            const normalTexture = new Texture(props.normalTexture, this.scene);
            normalTexture.uScale = props.normalTextureUV?.u ?? 8.5;
            normalTexture.vScale = props.normalTextureUV?.v ?? 8.5;
            material.bumpTexture = normalTexture;
            
            // Apply bump intensity if specified
            if (props.bumpIntensity !== undefined) {
                normalTexture.level = props.bumpIntensity;
                console.log(`Bump intensity set to: ${props.bumpIntensity}`);
            }
            
            if (operationId !== null) {
                this.trackTextureLoad(operationId);
                normalTexture.onLoadObservable.add(() => {
                    this.onTextureLoaded(operationId);
                });
            }
        }
        
        if (props.metallicRoughnessTexture) {
            const metallicTexture = new Texture(props.metallicRoughnessTexture, this.scene);
            metallicTexture.uScale = props.metallicRoughnessTextureUV?.u ?? 8.5;
            metallicTexture.vScale = props.metallicRoughnessTextureUV?.v ?? 8.5;
            material.metallicTexture = metallicTexture;
            
            if (operationId !== null) {
                this.trackTextureLoad(operationId);
                metallicTexture.onLoadObservable.add(() => {
                    this.onTextureLoaded(operationId);
                });
            }
        }
        
        if (props.aoTexture) {
            const aoTexture = new Texture(props.aoTexture, this.scene);
            aoTexture.uScale = props.aoTextureUV?.u ?? 8.5;
            aoTexture.vScale = props.aoTextureUV?.v ?? 8.5;
            material.ambientTexture = aoTexture;
            
            if (operationId !== null) {
                this.trackTextureLoad(operationId);
                aoTexture.onLoadObservable.add(() => {
                    this.onTextureLoaded(operationId);
                });
            }
        }
        
        if (props.bumpTexture) {
            const detailTexture = new Texture(props.bumpTexture, this.scene);
            detailTexture.uScale = props.bumpTextureUV?.u ?? 8.5;
            detailTexture.vScale = props.bumpTextureUV?.v ?? 8.5;
            if (material.albedoTexture) {
                (material.albedoTexture as Texture).level = 0.2;
                detailTexture.level = 0.8;
            } else {
                material.albedoTexture = detailTexture;
            }
            
            if (operationId !== null) {
                this.trackTextureLoad(operationId);
                detailTexture.onLoadObservable.add(() => {
                    this.onTextureLoaded(operationId);
                });
            }
        }
        
        return material;
    }

    private createLEDMaterialFromConfig(materialConfig: LEDMaterial): PBRMaterial {
        console.log(`Creating LED material: ${materialConfig.displayName}`);
        const material = new PBRMaterial(materialConfig.id, this.scene);
        const props = materialConfig.properties;
        
        // Support albedoColor specified either as 0-1 floats or 0-255 ints.
        {
            const [r, g, b] = props.albedoColor;
            const scale = (r > 1 || g > 1 || b > 1) ? 255 : 1;
            material.albedoColor = new Color3(r / scale, g / scale, b / scale);
        }
        material.metallic = props.metallic;
        material.roughness = props.roughness;
        material.environmentIntensity = props.environmentIntensity || 0.3;
        
        // Add emissive properties for LED glow effect
        if (props.emissiveColor) {
            // Support emissiveColor specified either as 0-1 floats or 0-255 ints.
            const [r, g, b] = props.emissiveColor;
            const scale = (r > 1 || g > 1 || b > 1) ? 255 : 1;
            material.emissiveColor = new Color3(r / scale, g / scale, b / scale);
            material.emissiveIntensity = props.emissiveIntensity || 1.0;
        }
        
        return material;
    }

    private async preloadHardwareMaterials(): Promise<void> {
        console.log('Pre-loading hardware and child mesh materials...');

        // Collect all unique child mesh node material paths from the shared config
        const uniquePaths = new Set<string>();
        for (const cfg of Object.values(ENCLOSURE_CHILD_MESH_MATERIALS)) {
            uniquePaths.add(cfg.nodeMaterialPath);
        }

        // Preload each unique path once, keyed by path so they can be shared
        for (const path of Array.from(uniquePaths)) {
            if (!this.enclosureChildMaterials.has(path)) {
                try {
                    console.log(`Loading enclosure child mesh node material: ${path}`);
                    const nodeMaterial = await this.loadNodeMaterial(`encChild_${path}`, path);
                    this.enclosureChildMaterials.set(path, nodeMaterial);
                } catch (error) {
                    console.error(`Failed to load enclosure child mesh node material: ${path}`, error);
                }
            }
        }
    }

    private createHardwarePBRMaterial(type: 'metal' | 'plastic'): PBRMaterial {
        const config = ENCLOSURE_HARDWARE_MATERIALS[type];
        const material = new PBRMaterial(config.id, this.scene);
        const props = config.properties;
        
        material.albedoColor = new Color3(props.albedoColor[0], props.albedoColor[1], props.albedoColor[2]);
        material.metallic = props.metallic;
        material.roughness = props.roughness;
        material.environmentIntensity = props.environmentIntensity;
        
        return material;
    }

    private getHardwareMaterial(type: 'metal' | 'plastic'): Material | null {
        const materialId = ENCLOSURE_HARDWARE_MATERIALS[type].id;
        const material = this.hardwareMaterials.get(materialId);
        
        if (!material) {
            console.warn(`Hardware material ${materialId} not found, may still be loading`);
            return null;
        }
        
        return material;
    }

    private applyMaterialsToMesh(mesh: AbstractMesh, knobMaterial: Material, specialMaterials: Map<string, PBRMaterial>, childMeshNodeMaterials?: Map<string, NodeMaterial>): void {
        // Apply main material to the mesh itself
        console.log(`Applying material to mesh ${mesh.name}:`, knobMaterial);
        console.log(`Material type:`, knobMaterial.constructor.name);
        mesh.material = knobMaterial;
        
        // Apply materials to child meshes
        mesh.getChildMeshes().forEach(childMesh => {
            const meshName = childMesh.name.toLowerCase();
            
            // Check for child mesh node materials first (highest priority)
            if (childMeshNodeMaterials) {
                for (const [targetMeshName, nodeMaterial] of Array.from(childMeshNodeMaterials.entries())) {
                    if (meshName.includes(targetMeshName.toLowerCase())) {
                        console.log(`Applying node material to child mesh ${childMesh.name} (matched: ${targetMeshName})`);
                        childMesh.material = nodeMaterial;
                        return;
                    }
                }
            }
            
            // Check for specific child mesh special materials
            for (const [targetMeshName, material] of Array.from(specialMaterials.entries())) {
                if (meshName.includes(targetMeshName.toLowerCase())) {
                    childMesh.material = material;
                    return;
                }
            }
            
            // Default to main material
            childMesh.material = knobMaterial;
        });
    }

    private async ensureKnobTypeBuilt(knobTypeId: string, enabled: boolean): Promise<void> {
        const existing = this.knobTypeNodes.get(knobTypeId);
        if (existing) {
            existing.parent.setEnabled(enabled);
            if (enabled) {
                // Safety: if any meshes were ever disabled individually, re-enable them.
                existing.meshes.forEach(m => m.setEnabled(true));
                this.currentKnobsParent = existing.parent;
                this.knobMeshes = existing.meshes;
                this.centerCameraTargetOnModel();
            }
            return;
        }

        const built = await this.buildKnobTypeNodes(knobTypeId, enabled);
        this.knobTypeNodes.set(knobTypeId, built);

        if (enabled) {
            this.currentKnobsParent = built.parent;
            this.knobMeshes = built.meshes;
            this.centerCameraTargetOnModel();
        }
    }

    private async buildKnobTypeNodes(knobTypeId: string, initiallyEnabled: boolean): Promise<{ parent: TransformNode; meshes: AbstractMesh[] }> {
        const knobType = KNOB_CONFIGURATION.find(k => k.id === knobTypeId);
        if (!knobType) {
            console.error(`Knob type ${knobTypeId} not found`);
            return { parent: new TransformNode(`knobsParent_${knobTypeId}_missing`, this.scene), meshes: [] };
        }

        console.log(`Loading knob model: ${knobType.modelPath}`);
        const result = await SceneLoader.ImportMeshAsync(
            "",
            "/models/Knobs/",
            knobType.modelPath,
            this.scene
        );

        const meshes = result.meshes;
        console.log(`Model loaded successfully, meshes: ${meshes.length}`);

        if (meshes.length === 0) {
            return { parent: new TransformNode(`knobsParent_${knobTypeId}_empty`, this.scene), meshes: [] };
        }

        const rootMesh = meshes[0];
        console.log('Root mesh:', rootMesh.name);

        // Create initial material (first option for this knob type)
        const initialMaterial = knobType.materials[0];
        let knobMaterial = this.knobMaterials.get(initialMaterial.id);
        if (!knobMaterial) {
            knobMaterial = await this.createMaterialFromConfig(initialMaterial);
            this.knobMaterials.set(initialMaterial.id, knobMaterial);
            await this.waitForMaterialTextures(knobMaterial);
        }

        // Prepare special materials map
        const specialMaterialsMap = new Map<string, PBRMaterial>();

        // Add indicator material if specified
        if (initialMaterial.indicatorMaterial) {
            const indicatorKey = `special_${initialMaterial.indicatorMaterial}`;
            let indicatorMaterial = this.specialMaterials.get(indicatorKey);

            if (!indicatorMaterial) {
                indicatorMaterial = this.createSpecialMaterial(initialMaterial.indicatorMaterial);
                this.specialMaterials.set(indicatorKey, indicatorMaterial);
            }

            specialMaterialsMap.set('indicator', indicatorMaterial);
        }

        // Add child mesh materials if specified
        if (initialMaterial.childMeshMaterials) {
            for (const [meshName, materialType] of Object.entries(initialMaterial.childMeshMaterials)) {
                const specialKey = `special_${materialType}`;
                let specialMaterial = this.specialMaterials.get(specialKey);

                if (!specialMaterial) {
                    specialMaterial = this.createSpecialMaterial(materialType);
                    this.specialMaterials.set(specialKey, specialMaterial);
                }

                specialMaterialsMap.set(meshName, specialMaterial);
            }
        }

        // Prepare child mesh node materials map
        const childMeshNodeMaterialsMap = new Map<string, NodeMaterial>();

        // Load child mesh node materials if specified
        if (initialMaterial.childMeshNodeMaterials) {
            for (const [meshName, nodeMaterialConfig] of Object.entries(initialMaterial.childMeshNodeMaterials)) {
                const materialKey = `${initialMaterial.id}_${meshName}`;
                let childNodeMaterial = this.childMeshNodeMaterials.get(materialKey);

                if (!childNodeMaterial) {
                    try {
                        console.log(`Loading child mesh node material for ${meshName} from ${nodeMaterialConfig.nodeMaterialPath}`);
                        childNodeMaterial = await this.loadNodeMaterial(materialKey, nodeMaterialConfig.nodeMaterialPath);
                        this.childMeshNodeMaterials.set(materialKey, childNodeMaterial);
                        await this.waitForMaterialTextures(childNodeMaterial);
                    } catch (error) {
                        console.error(`Failed to load child mesh node material for ${meshName}, skipping:`, error);
                        continue;
                    }
                }

                childMeshNodeMaterialsMap.set(meshName, childNodeMaterial);
            }
        }

        // Apply materials to all meshes in the model
        meshes.forEach(mesh => {
            this.applyMaterialsToMesh(mesh, knobMaterial!, specialMaterialsMap, childMeshNodeMaterialsMap);
            mesh.isVisible = true;
            // IMPORTANT: Do not disable meshes individually during preload.
            // We only toggle the parent node, otherwise the meshes can stay disabled
            // even after enabling the parent later (Babylon keeps per-mesh enabled state).
            mesh.setEnabled(true);
        });

        // Create a parent node for all knobs for this type
        const knobsParent = new TransformNode(`knobsParent_${knobTypeId}`, this.scene);
        knobsParent.position = new Vector3(0, .2, 0);
        knobsParent.setEnabled(initiallyEnabled);

        // Ensure every top-level imported mesh is under the knobs parent so enable/disable works reliably.
        // (Some GLBs can have multiple root meshes.)
        meshes.forEach(m => {
            if (!m.parent) {
                m.parent = knobsParent;
            }
        });

        // Position center knob
        rootMesh.parent = knobsParent;
        rootMesh.position = new Vector3(0, -.004, 0.1);
        rootMesh.scaling = new Vector3(7, 7, 7);

        // Clone and position left knob
        const leftKnob = rootMesh.clone(`leftKnob_${knobTypeId}`, knobsParent, false);
        if (leftKnob) {
            leftKnob.position = new Vector3(-.1325, .0835, .1);
            leftKnob.scaling = new Vector3(7, 7, 7);
            meshes.push(leftKnob as AbstractMesh);
        }

        // Clone and position right knob
        const rightKnob = rootMesh.clone(`rightKnob_${knobTypeId}`, knobsParent, false);
        if (rightKnob) {
            rightKnob.position = new Vector3(.1325, .0835, .1);
            rightKnob.scaling = new Vector3(7, 7, 7);
            meshes.push(rightKnob as AbstractMesh);
        }

        console.log('Model position:', rootMesh.position);
        console.log('Model scale:', rootMesh.scaling);
        console.log('Total knob meshes:', meshes.length);

        return { parent: knobsParent, meshes };
    }

    private centerCameraTargetOnModel(): void {
        const isMobile =
            window.matchMedia?.('(max-width: 768px)')?.matches ||
            /iPhone|iPad|iPod/i.test(navigator.userAgent);
        this.camera.setTarget(isMobile ? new Vector3(0, -0.05, 0) : Vector3.Zero());
    }

    private async loadEnclosureModel(): Promise<void> {
        // Load enclosure model
        try {
            const result = await SceneLoader.ImportMeshAsync(
                "",
                "/models/Fixed/",
                "ts4.glb",
                this.scene
            );

            const meshes = result.meshes;
            console.log('Enclosure loaded successfully, meshes:', meshes.length);
            if (meshes.length > 0) {
                // Use root mesh as the enclosure mesh; child meshes are handled
                // individually via childMeshNodeMaterials in the config.
                meshes.forEach(mesh => console.log('Enclosure mesh:', mesh.name));
                this.enclosureMesh = meshes[0];

                const rootMesh = meshes[0];

                // Position and scale the enclosure
                rootMesh.position = new Vector3(0, -.02, 0);
                rootMesh.scaling = new Vector3(7, 7, 7);

                // Fix mirroring by flipping the scale on X axis
                rootMesh.scaling.x *= -1;

                // Apply initial materials
                await this.switchEnclosureMaterial(this.currentEnclosureMaterial);
                // Apply initial LED material to the LED child mesh
                this.switchLEDMaterial(this.currentLEDMaterial);
            }
        } catch (e) {
            console.error('Error loading enclosure model:', e);
        }
    }

    private async loadLEDModel(): Promise<void> {
        // Load LED model
        try {
            const result = await SceneLoader.ImportMeshAsync(
                "",
                "/models/Fixed/",
                "led.glb",
                this.scene
            );

            const meshes = result.meshes;
            console.log('LED loaded successfully, meshes:', meshes.length);
            if (meshes.length > 0) {
                const rootMesh = meshes[0];
                this.ledMesh = rootMesh;

                // Position and scale the LED
                rootMesh.position = new Vector3(-.341, -.24450, .1675); // Position above the knobs
                rootMesh.scaling = new Vector3(6.6, 6.6, 6.6);

                // Apply initial LED material
                this.switchLEDMaterial(this.currentLEDMaterial);

                console.log('LED positioned at:', rootMesh.position);
            }
        } catch (e) {
            console.error('Error loading LED model:', e);
        }
    }

    startRenderLoop(): void {
        this.engine.runRenderLoop(() => {
            this.scene.render();
        });
    }

    stopRenderLoop(): void {
        this.engine.stopRenderLoop();
    }

    dispose(): void {
        if (this.glowLayer) {
            this.glowLayer.dispose();
            this.glowLayer = null;
        }
        this.scene.dispose();
        this.engine.dispose();
    }

    getScene(): Scene {
        return this.scene;
    }

    // Loading state management
    setLoadingCallback(callback: (isLoading: boolean) => void): void {
        this.onLoadingChange = callback;
        // Immediately inform the caller of the current state so the overlay
        // can be shown even if scene creation started before the callback was set.
        this.emitLoadingState();
    }

    private emitLoadingState(): void {
        if (!this.onLoadingChange) return;
        const shouldShow = this.hardLoading || this.pendingTextureLoads > 0;
        this.onLoadingChange(shouldShow);
    }

    private startLoadingOperation(): number {
        // Cancel any previous loading operation
        this.currentOperationId++;
        this.pendingTextureLoads = 0;
        this.emitLoadingState();
        
        return this.currentOperationId;
    }

    private trackTextureLoad(operationId: number): void {
        // Only track if this is the current operation
        if (operationId === this.currentOperationId) {
            this.pendingTextureLoads++;
        }
    }

    private onTextureLoaded(operationId: number): void {
        // Only process if this is the current operation
        if (operationId === this.currentOperationId) {
            this.pendingTextureLoads--;
            
            // If no more pending textures, hide loading
            if (this.pendingTextureLoads <= 0) {
                this.pendingTextureLoads = 0;
                this.emitLoadingState();
            }
        }
    }
}



        



        








